import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../ui/alert-dialog';
import {
  Presentation, Trash2, Edit, Search, Building2, Link2, FolderKanban,
} from 'lucide-react';
import ProjectLinkCell from '../shared/ProjectLinkCell';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../lib/api';
import { ExportDropdown } from '../shared/PageActions';
import { generateProposalPDF } from '../../lib/proposalPdfExport';
import { getDefaultCompanyInfo } from '../../lib/brandConfig';

const STATUS_COLORS = {
  draft:    { bg: '#f3f4f6', text: '#6b7280', dot: '#9ca3af' },
  sent:     { bg: '#dbeafe', text: '#2563eb', dot: '#3b82f6' },
  accepted: { bg: '#dcfce7', text: '#16a34a', dot: '#22c55e' },
  declined: { bg: '#fee2e2', text: '#dc2626', dot: '#ef4444' },
};

function StatusBadge({ status }) {
  const s = STATUS_COLORS[status] || STATUS_COLORS.draft;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold capitalize"
      style={{ background: s.bg, color: s.text }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.dot }} />
      {status || 'draft'}
    </span>
  );
}

export default function ProposalsTab({ onEditProposal }) {
  const navigate = useNavigate();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState(''); // '' = all
  const [projects, setProjects] = useState([]);

  // ── Fetch proposals from API ──────────────────────────────────────────────
  const fetchProposals = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/proposals`);
      setProposals(res.data.data || []);
    } catch (err) {
      console.error('Failed to fetch proposals:', err);
      toast.error('Failed to load proposals');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProposals();
  }, [fetchProposals]);

  // ── Projects, for resolving project_id → name and for the assign picker ────
  useEffect(() => {
    axios.get(`${API}/projects?limit=200`)
      .then(res => setProjects(res.data.data || []))
      .catch(() => {});
  }, []);

  const handleLinked = useCallback((proposalId, projectId) => {
    setProposals(prev => prev.map(p => (p.id === proposalId ? { ...p, project_id: projectId } : p)));
  }, []);

  // ── KPI counts ────────────────────────────────────────────────────────────
  const kpi = {
    total: proposals.length,
    draft: proposals.filter(p => (p.status || 'draft') === 'draft').length,
    sent: proposals.filter(p => p.status === 'sent').length,
    accepted: proposals.filter(p => p.status === 'accepted').length,
  };

  // ── Filtered proposals ────────────────────────────────────────────────────
  const filteredProposals = proposals.filter(p => {
    if (statusFilter && (p.status || 'draft') !== statusFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const clientName = p.variables?.client_name || '';
      const projectName = p.variables?.project_name || '';
      return (
        (p.proposal_no || '').toLowerCase().includes(q) ||
        clientName.toLowerCase().includes(q) ||
        projectName.toLowerCase().includes(q) ||
        (p.quote_no || '').toLowerCase().includes(q)
      );
    }
    return true;
  });

  // ── Delete proposal ───────────────────────────────────────────────────────
  const handleDeleteProposal = async (id) => {
    try {
      await axios.delete(`${API}/proposals/${id}`);
      setProposals(prev => prev.filter(p => p.id !== id));
      toast.success('Proposal deleted');
    } catch (err) {
      console.error('Failed to delete proposal:', err);
      toast.error('Failed to delete proposal');
    }
  };

  // ── Export ─────────────────────────────────────────────────────────────────
  const companyInfo = getDefaultCompanyInfo();

  const fetchFullProposal = async (id) => {
    try {
      const res = await axios.get(`${API}/proposals/${id}`);
      return res.data;
    } catch {
      return null;
    }
  };

  // generateProposalPDF is async — see QuotesTab for the same reasoning.
  const handleGeneratePdf = async (mode) => {
    if (filtered.length === 0) {
      toast.info('There are no proposals to export.');
      return;
    }
    const p = await fetchFullProposal(filtered[0].id);
    if (!p) {
      toast.error('Could not load that proposal to export.');
      return;
    }
    try {
      await generateProposalPDF(p, mode);
      if (mode === 'download') toast.success('PDF generated');
    } catch (err) {
      console.error('PDF generation failed:', err);
      toast.error('Failed to generate the PDF.');
    }
  };
  const handlePdfPreview = () => handleGeneratePdf('preview');
  const handlePdfDownload = () => handleGeneratePdf('download');
  const handlePrint = () => window.print();

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Presentation className="w-8 h-8 animate-pulse text-[#c9a84c]/40" />
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <h2 className="text-base sm:text-xl font-semibold text-[#1a1a2e]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          All Proposals
        </h2>
        <div className="flex items-center gap-2">
          <ExportDropdown onPdfPreview={handlePdfPreview} onPdfDownload={handlePdfDownload} onPrint={handlePrint} />
        </div>
      </div>

      {/* KPI bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        {[
          { label: 'Total', value: kpi.total, color: '#1a1a2e' },
          { label: 'Draft', value: kpi.draft, color: '#6b7280' },
          { label: 'Sent', value: kpi.sent, color: '#3b82f6' },
          { label: 'Accepted', value: kpi.accepted, color: '#22c55e' },
        ].map(item => (
          <div key={item.label} className="bg-white rounded-lg border border-[#e5e4df] px-4 py-3">
            <div className="text-xs font-semibold uppercase tracking-wider" style={{ color: item.color }}>{item.label}</div>
            <div className="text-2xl font-bold text-[#1a1a2e] mt-0.5">{item.value}</div>
          </div>
        ))}
      </div>

      {/* Search + Status filter */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9ca3af]" />
          <input
            type="text"
            placeholder="Search proposals..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-[#e5e4df] rounded-lg focus:outline-none focus:ring-1 focus:ring-[#c9a84c] focus:border-[#c9a84c] bg-white"
          />
        </div>
        <div className="flex gap-1.5">
          {['', 'draft', 'sent', 'accepted', 'declined'].map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className="px-3 py-1.5 text-xs font-semibold rounded-full transition-colors capitalize"
              style={{
                background: statusFilter === s ? '#1a1a2e' : 'transparent',
                color: statusFilter === s ? '#fff' : '#6b7280',
                border: statusFilter === s ? '1px solid #1a1a2e' : '1px solid #e5e4df',
              }}
            >
              {s || 'All'}
            </button>
          ))}
        </div>
      </div>

      {/* Empty state */}
      {filteredProposals.length === 0 ? (
        <div className="text-center py-16">
          <Presentation className="w-16 h-16 mx-auto text-[#c9a84c]/40 mb-4" />
          <p className="text-[#6b7280] text-lg mb-2">No proposals yet</p>
          <p className="text-[#6b7280]/60 text-sm">
            Proposals are created from inside a project
          </p>
        </div>
      ) : (
        <div className="space-y-1">
          {/* Desktop header */}
          <div
            className="hidden sm:grid gap-3 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-[#6b7280]"
            style={{ gridTemplateColumns: '130px 90px 1fr 1fr 100px 90px 108px' }}
          >
            <span>Proposal No</span>
            <span>Status</span>
            <span>Client</span>
            <span>Project</span>
            <span>Quote Ref</span>
            <span>Date</span>
            <span className="text-right">Actions</span>
          </div>

          {filteredProposals.map((p) => {
            const clientName = p.variables?.client_name || '—';
            const projectName = p.variables?.project_name || '—';
            const proposalDate = p.variables?.proposal_date || '—';

            return (
              <div key={p.id}>
                {/* Desktop row */}
                <div
                  className="hidden sm:grid gap-3 px-4 py-3 rounded-md hover:bg-[#f3f2ef] transition-colors cursor-pointer items-center"
                  style={{
                    gridTemplateColumns: '130px 90px 1fr 1fr 100px 90px 108px',
                    borderBottom: '1px solid rgba(201,168,76,0.1)',
                  }}
                  onClick={() => onEditProposal(p.id)}
                >
                  <span className="font-mono font-semibold text-sm text-[#1a1a2e]">{p.proposal_no || '—'}</span>
                  <StatusBadge status={p.status || 'draft'} />
                  <span className="text-sm truncate flex items-center gap-1.5">
                    {p.client_id && (
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/clients/${p.client_id}`); }}
                        className="p-0.5 rounded hover:bg-[#c9a84c]/10 text-[#c9a84c] transition-colors"
                        title="View Client"
                      >
                        <Building2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {clientName}
                  </span>
                  <ProjectLinkCell
                    docId={p.id}
                    collection="proposals"
                    projectId={p.project_id}
                    fallbackText={p.variables?.project_name}
                    projects={projects}
                    onLinked={handleLinked}
                  />
                  <span className="text-sm">
                    {p.quote_id ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/quote/${p.quote_id}`); }}
                        className="inline-flex items-center gap-1 font-mono text-[#6b7280] hover:text-[#c9a84c] transition-colors"
                      >
                        <Link2 className="w-3 h-3" />
                        {p.quote_no || '—'}
                      </button>
                    ) : (
                      <span className="font-mono text-[#9ca3af]">—</span>
                    )}
                  </span>
                  <span className="text-sm text-[#6b7280]">{proposalDate}</span>
                  <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
                    <button onClick={() => onEditProposal(p.id)} className="p-1.5 rounded hover:bg-[#c9a84c]/10 text-[#6b7280] hover:text-[#1a1a2e] transition-colors" title="Edit Proposal"><Edit className="w-4 h-4" /></button>
                    {p.project_id && (
                      <button
                        onClick={() => navigate(`/projects/${p.project_id}`)}
                        className="p-1.5 rounded transition-colors text-emerald-600 hover:bg-emerald-50"
                        title="View Project"
                      >
                        <FolderKanban className="w-4 h-4" />
                      </button>
                    )}
                    <AlertDialog>
                      <AlertDialogTrigger asChild><button className="p-1.5 rounded hover:bg-red-50 text-[#6b7280] hover:text-[#dc2626] transition-colors"><Trash2 className="w-4 h-4" /></button></AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Delete Proposal</AlertDialogTitle><AlertDialogDescription>Are you sure you want to delete {p.proposal_no || 'this proposal'}? This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => handleDeleteProposal(p.id)} className="bg-[#dc2626] hover:bg-[#b91c1c]">Delete</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>

                {/* Mobile card */}
                <div
                  className="sm:hidden px-3 py-3 cursor-pointer hover:bg-[#f3f2ef] transition-colors rounded-md"
                  style={{ borderBottom: '1px solid rgba(201,168,76,0.12)' }}
                  onClick={() => onEditProposal(p.id)}
                >
                  <div className="flex justify-between items-start">
                    <span className="font-mono font-semibold text-sm text-[#1a1a2e]">{p.proposal_no || '—'}</span>
                    <StatusBadge status={p.status || 'draft'} />
                  </div>
                  <div className="text-sm font-medium text-[#1a1a2e] mt-0.5 flex items-center gap-1.5">
                    {p.client_id && (
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/clients/${p.client_id}`); }}
                        className="p-0.5 rounded hover:bg-[#c9a84c]/10 text-[#c9a84c] transition-colors"
                      >
                        <Building2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {clientName}
                  </div>
                  <div className="text-xs text-[#6b7280] truncate">{projectName}</div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-xs text-[#9ca3af]">{proposalDate}</span>
                    <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                      <button onClick={() => onEditProposal(p.id)} className="p-2 rounded hover:bg-[#c9a84c]/10 text-[#6b7280] hover:text-[#1a1a2e] transition-colors" title="Edit"><Edit className="w-4 h-4" /></button>
                      {p.project_id && (
                        <button
                          onClick={() => navigate(`/projects/${p.project_id}`)}
                          className="p-2 rounded transition-colors text-emerald-600 hover:bg-emerald-50"
                          title="View Project"
                        >
                          <FolderKanban className="w-4 h-4" />
                        </button>
                      )}
                      <AlertDialog>
                        <AlertDialogTrigger asChild><button className="p-2 rounded hover:bg-red-50 text-[#6b7280] hover:text-[#dc2626] transition-colors"><Trash2 className="w-4 h-4" /></button></AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader><AlertDialogTitle>Delete Proposal</AlertDialogTitle><AlertDialogDescription>Are you sure you want to delete {p.proposal_no || 'this proposal'}? This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => handleDeleteProposal(p.id)} className="bg-[#dc2626] hover:bg-[#b91c1c]">Delete</AlertDialogAction></AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
